import { useState, useCallback, useEffect, useRef } from "react";

interface UseColumnResizeOptions {
  /** Larguras restauradas do estado persistido (ex.: storageKey da lista). */
  initialWidths?: Record<string, number>;
  /** Chamado ao soltar o arraste, com o mapa final de larguras (para persistir). */
  onWidthsChange?: (widths: Record<string, number>) => void;
}

export function useColumnResize(options: UseColumnResizeOptions = {}) {
  const { initialWidths, onWidthsChange } = options;
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(initialWidths ?? {});
  const resizingRef = useRef<{ key: string; startX: number; startWidth: number } | null>(null);
  const onWidthsChangeRef = useRef(onWidthsChange);

  useEffect(() => {
    onWidthsChangeRef.current = onWidthsChange;
  }, [onWidthsChange]);

  // Reidrata quando a fonte persistida muda (ex.: troca de storageKey / clear all).
  useEffect(() => {
    setColumnWidths(initialWidths ?? {});
    // Comparamos o conteúdo, não a referência, para evitar re-set a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialWidths ?? {})]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!resizingRef.current) return;

    const { key, startX, startWidth } = resizingRef.current;
    const diff = e.clientX - startX;
    const newWidth = Math.max(50, startWidth + diff); // Mínimo de 50px para não sumir

    setColumnWidths((prev) => ({
      ...prev,
      [key]: newWidth,
    }));
  }, []);

  const handleMouseUp = useCallback(() => {
    resizingRef.current = null;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";

    // Persiste a largura final lendo o estado mais recente.
    setColumnWidths((prev) => {
      onWidthsChangeRef.current?.(prev);
      return prev;
    });
  }, [handleMouseMove]);

  const startResize = useCallback(
    (e: React.MouseEvent, key: string) => {
      e.preventDefault();
      e.stopPropagation();

      // Pega a largura atual do elemento pai (th) para iniciar o drag suavemente
      const headerCell = (e.target as HTMLElement).closest("th");
      if (!headerCell) return;

      const startWidth = headerCell.getBoundingClientRect().width;

      resizingRef.current = {
        key,
        startX: e.clientX,
        startWidth,
      };

      // Adiciona listeners globais para permitir arrastar fora do header
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize"; // Feedback visual global
      document.body.style.userSelect = "none"; // Evita selecionar texto enquanto arrasta
    },
    [handleMouseMove, handleMouseUp],
  );

  // Limpeza de segurança
  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return { columnWidths, startResize };
}
